import React from "react";
import { Popover, Position, PopoverInteractionKind } from "@blueprintjs/core";
import { ValidationErrorsPopover } from "./ValidationErrorsPopover";
import "./ValidationErrorsCounts.scss";
import { DomainValidationError } from "@/types/FinancialApplicationValidationState";
import classNames from "classnames";

export enum ValidationErrorDisplayMode {
  // OnlyIsRequiredErrorCode = "only-required",
  // OnlyNotIsRequiredErrorCode = "only-non-required",
  // AllErrors = "all-errors",
  // AllErrorsSeparateBadges = "all-errors-separate-badges",
  RemainingFromTotal = "remaining-from-total",
  Remaining = "remaining",
}

interface ValidationErrorsCountsProps {
  /** List of validation errors to display */
  validationErrors: DomainValidationError[];
  /** The mode to filter which errors to display */
  displayMode: ValidationErrorDisplayMode;
  /** Optional label to display before the count */
  label?: string;
  /** Optional CSS class name for custom styling */
  className?: string;
  /** Whether to enable the popover functionality */
  // showErrorDetails?: boolean;
  /** Label to display in the popover header. If not provided, will use the label prop. */
  popoverLabel?: string;
  /** Function to get display name for a property name */
  getPropertyDisplayName?: (error: DomainValidationError) => string;
  /** Whether to render the component when there are no errors. Defaults to true. */
  renderZeroErrors?: boolean;
  /** An optional function that when provided will navigate to the field that has the error. */
  navigateToField?: (error: DomainValidationError) => Promise<void> | void;
  /** The label to display for the navigate to field button. */
  navigateToFieldLabel?: string;
  /** Whether to show the section title in the popover. */
  showSectionTitle?: boolean;
  /** Optional CSS class name for the popover. */
  popoverClassName?: string;
  /** The total number of required fields. */
  totalRequiredFieldCount?: number;
}

/**
 * ValidationErrorsCounts component displays validation errors with visual distinctions
 * between required and non-required errors.
 *
 * @example
 * // Display only required field errors
 * <ValidationErrorsCounts
 *   validationErrors={errors}
 *   displayMode={ValidationErrorDisplayMode.OnlyIsRequiredErrorCode}
 *   showErrorDetails={true}
 * />
 *
 * @example
 * // Display all errors with custom property name mapping
 * <ValidationErrorsCounts
 *   validationErrors={errors}
 *   displayMode={ValidationErrorDisplayMode.AllErrors}
 *   showErrorDetails={true}
 *   getPropertyDisplayName={(prop) => getFieldDisplayName(prop)}
 * />
 *
 * @example
 * // Hide component when there are no errors
 * <ValidationErrorsCounts
 *   validationErrors={errors}
 *   displayMode={ValidationErrorDisplayMode.AllErrors}
 *   renderZeroErrors={false}
 * />
 *
 * @example
 * // Display separate badges with separate popovers for required vs non-required errors
 * <ValidationErrorsCounts
 *   validationErrors={errors}
 *   displayMode={ValidationErrorDisplayMode.AllErrorsSeparateBadges}
 *   showErrorDetails={true}
 * />
 */
export const ValidationErrorsCounts: React.FC<ValidationErrorsCountsProps> = ({
  validationErrors,
  displayMode,
  label,
  className = "",
  popoverLabel,
  getPropertyDisplayName,
  renderZeroErrors = false,
  navigateToField,
  showSectionTitle = false,
  popoverClassName,
  totalRequiredFieldCount,
}) => {
  const popoverRef = React.useRef<Popover>(null);
  // Filter errors based on display mode
  const filteredErrors = React.useMemo(() => {
    return validationErrors;
  }, [validationErrors, displayMode]);

  const errorCount = filteredErrors.length;
  const hasErrorDetails = errorCount > 0;

  // If there are no errors and renderZeroErrors is false, don't render anything
  if (errorCount === 0 && !renderZeroErrors) {
    return null;
  }

  // todo this doesnt work but the bones are there.
  const navigateToFieldAndClosePopover = navigateToField
    ? (error: DomainValidationError) => {
        popoverRef.current?.setState({ isOpen: false });
        navigateToField(error);
      }
    : undefined;

  // For AllErrorsSeparateBadges mode, we need to handle separate popovers
    return (
    <div className={`validation-errors-counts ${className}`}>
      <Popover
        content={
          <ValidationErrorsPopover
            errors={filteredErrors}
            popoverLabel={popoverLabel}
            label={label}
            getPropertyDisplayName={getPropertyDisplayName}
            navigateToField={navigateToFieldAndClosePopover}
            showSectionTitle={showSectionTitle}
            totalRequiredFieldCount={totalRequiredFieldCount}
          />
        }
        position={Position.BOTTOM}
        interactionKind={PopoverInteractionKind.HOVER}
        portalClassName={`validation-errors-popover-portal ${popoverClassName || ""}`}
        isOpen={hasErrorDetails ? undefined : false}
      >
        <span
          className={`validation-errors ${hasErrorDetails ? "validation-errors--interactive" : ""}`}
        >
          {label && <span className="validation-errors__label">{label}</span>}
          {displayMode === ValidationErrorDisplayMode.Remaining && (
            <span className="validation-errors__count validation-errors__count--required e-badge e-badge-pill">
              <span className={classNames("validation-errors__count-number", {
                "validation-errors__count-number--complete": errorCount <= 0
              })}>{errorCount}</span>
            </span>
            )}
          {displayMode === ValidationErrorDisplayMode.RemainingFromTotal && (
            <span className="validation-errors__count validation-errors__count--required e-badge e-badge-pill">
              <span className={classNames("validation-errors__count-number", {
                "validation-errors__count-number--complete": errorCount <= 0
              })}>{errorCount}</span>
              <span className="validation-errors__count-divider">/</span>
              <span className="validation-errors__count-total">{!!totalRequiredFieldCount ? `${totalRequiredFieldCount}` : "-"}</span>
            </span>
          )}
        </span>
      </Popover>
    </div>
  );
};

export default ValidationErrorsCounts;
